export type JSONSerializable =
  | string
  | number
  | boolean
  | null
  | JSONSerializable[]
  | { [key: string]: JSONSerializable };

/**
 * Iteratively merge two records whose values are {@link JSONSerializable}-compatible objects.
 * 
 * Conflicting values are resolved by taking the value in the second record.
 * Non-nullish values take precedence over nullish values.
 */
export function deepMerge(
  record1: Record<string, JSONSerializable>,
  record2: Record<string, JSONSerializable>
): Record<string, JSONSerializable> {
  const result: Record<string, JSONSerializable> = JSON.parse(
    JSON.stringify(record1)
  );

  for (const key of Object.keys(record2)) {
    const val2 = record2[key];
    const val1 = result[key];

    if (Array.isArray(val1) && Array.isArray(val2)) {
      result[key] = mergeArrays(val1, val2);
    } else if (
      val1 &&
      typeof val1 === 'object' &&
      !Array.isArray(val1) &&
      val2 &&
      typeof val2 === 'object' &&
      !Array.isArray(val2)
    ) {
      result[key] = deepMerge(
        val1 as Record<string, JSONSerializable>,
        val2 as Record<string, JSONSerializable>
      );
    } else {
      result[key] = JSON.parse(JSON.stringify(val2));
    }
  }

  return result;
}

export function mergeArrays(
  arr1: JSONSerializable[],
  arr2: JSONSerializable[]
): JSONSerializable[] {
  const maxLength = Math.max(arr1.length, arr2.length);
  const result: JSONSerializable[] = [];

  for (let i = 0; i < maxLength; i++) {
    const val1 = arr1[i];
    const val2 = arr2[i];

    if (val2 === undefined) {
      result[i] = val1;
    } else if (val1 === undefined) {
      result[i] = val2;
    } else if (Array.isArray(val1) && Array.isArray(val2)) {
      result[i] = mergeArrays(val1, val2);
    } else if (
      val1 &&
      typeof val1 === 'object' &&
      !Array.isArray(val1) &&
      val2 &&
      typeof val2 === 'object' &&
      !Array.isArray(val2)
    ) {
      result[i] = deepMerge(
        val1 as Record<string, JSONSerializable>,
        val2 as Record<string, JSONSerializable>
      );
    } else {
      result[i] = val2;
    }
  }

  return result;
}
